import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Card, Button, Tooltip, IconButton } from '@material-ui/core';
import { Fullscreen as FullscreenIcon } from '@material-ui/icons';

import { useDebounce } from '../../../common/Debounce.hooks';
import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import CardTitleContent from '../../../common/card/CardTitleContent';
import { useUserContext } from '../../../user/User.context';
import { TemplateType } from '../../template/Template.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import DocumentUpload, { DocumentUploadRef } from '../DocumentUpload';
import { useUpdateFile } from '../Document.hook';
import { BaseDocumentCardProps } from '../Document.interface';
import DocumentViewer from './DocumentViewer';
import DocumentDownloadButton from './DocumentDownloadButton';
import DocumentFullscreenDialog from './DocumentFullscreenDialog';

const StyledCard = styled(Card)`
  flex: 1;
  display: flex;
  flex-direction: column;
  .content-flex {
    flex: 1;
  }
  .iframe {
    width: 100%;
    height: 100%;
  }
`;

export interface DocumentViewerCardProps extends BaseDocumentCardProps {
  title?: string;
  storageKey: string;
  fileSize?: number;
}

const DocumentViewerCard: React.FC<DocumentViewerCardProps> = (
  props: DocumentViewerCardProps
) => {
  const { document, storageKey, fileSize, title, onChange, onUpdate, onError } =
    props;
  const uploadRef = useRef<DocumentUploadRef>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fullscreen, setFullScreen] = useState(false);
  const can = useUserContext().permissions.file;
  const { isFieldDisabled } = useTaskEntityFields(document.task);
  const [{ data: updatedFile, error, isLoading }, updateDocument] =
    useUpdateFile(document.id, document.isFolder);
  // once file is updated, debounce before setting state
  // to avoid fetching S3 file just after upload
  const newFile = useDebounce(updatedFile, 200);
  const disabled = can.not.update || isFieldDisabled('uploadNewFile');
  const allowTemplateTypes = ['.doc', '.docx', '.pdf'].some(extension => {
    return storageKey.toLowerCase().includes(extension);
  })
    ? [TemplateType.DocumentApproval]
    : undefined;

  useEffect(() => {
    if (newFile) {
      // update file on parent
      onChange?.(newFile);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newFile]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <StyledCard>
      <CardTitleContent>
        <CardTitle>
          Document
          <IconButton size='small' onClick={() => setFullScreen(true)}>
            <FullscreenIcon />
          </IconButton>
        </CardTitle>
        <Tooltip
          arrow
          title={
            disabled
              ? 'Cannot upload due to status or insufficient permissions.'
              : ''
          }
        >
          <span className='btn-container'>
            <Button
              color='secondary'
              variant='contained'
              disabled={disabled}
              onClick={() => !disabled && fileInputRef.current?.click?.()}
            >
              Upload New File
            </Button>
          </span>
        </Tooltip>
        <DocumentDownloadButton
          title={title}
          storageKey={storageKey}
          allowTemplateTypes={allowTemplateTypes}
          file={{ id: document.id, version: document.fileVersion?.version }}
        />
      </CardTitleContent>
      <CardContent className='content-flex'>
        <DocumentViewer
          storageKey={storageKey}
          title={title}
          fileSize={fileSize}
        />
      </CardContent>

      <input
        hidden
        type='file'
        ref={fileInputRef}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const files = event.target.files;
          if (files && files.length > 0) {
            const file = files[0];
            uploadRef.current?.(file);
            // clear input field
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }
        }}
      />
      <DocumentUpload
        innerRef={uploadRef}
        isLoading={isLoading}
        loadingMessage={isLoading && 'Updating document...'}
        onError={onError}
        onSuccess={updateDocument}
      />

      <DocumentFullscreenDialog
        open={fullscreen}
        storageKey={storageKey}
        title={title}
        fileSize={fileSize}
        onClose={() => setFullScreen(false)}
      />
    </StyledCard>
  );
};

export default DocumentViewerCard;
